/**
 * The message contract between the Loupe shell and the SDK inside the
 * reviewed app.
 *
 * This file is the contract itself - both sides import it, so a change on
 * one side does not compile on the other. Concept sec 7 calls the anchor a
 * "contract between two codebases"; this is where that contract lives.
 *
 * Everything travels by postMessage, so every payload must be structured-
 * clonable: plain objects, arrays, strings, numbers. No class instances, no
 * functions, no DOM nodes.
 */

/**
 * 3 since 2026-09-10: pins carry the entry itself (title, text, kind, whether
 * it is the viewer's own), the view carries its scroll position, and the hub
 * announces edits (`loupe:note-updated`). Every addition is optional, so a
 * side on 2 keeps working and the shell names the mismatch (ADR-C-010).
 *
 * 4 since 2026-09-14: a `loupe:ready` sent in answer to a `loupe:hello` says
 * so (`reply`), and the shell greets only a ready that is not an answer. Until
 * then each side answered the other forever - D-017, measured at ~16,000
 * messages a second in the frame and ~2,400 WebSocket frames a second through
 * the hub, for as long as a review was open. A side on 3 still loops with a
 * side on 4, and the badge names it.
 *
 * 5 since 2026-09-17: the hub says `loupe:server-restart` to every socket before
 * Loupe's own process stops (D-024), and closes it with `RESTART_CLOSE`. A side
 * on 4 ignores the message and reconnects as before - it just cannot say why.
 *
 * 6 since 2026-09-17, migration 0009: the Pruefabschnitt is gone. The socket
 * joins a CONNECTION (`/ws?connection=`), the envelope names its `room`, and
 * the window is handed `loupe_connection` instead of `loupe_session`. Not
 * additive - a side on 5 cannot join a room at all. Both halves are served by
 * the same Loupe, so they move together; only a window left open across the
 * update is on 5, and reopening it is the fix.
 *
 * 7 since 2026-09-25 (Holger's first feedback round): `loupe:set-prefs` hands
 * the SDK the reviewer's keys and marks - the toggle key, the held modifiers
 * that switch the mode for as long as they are down, whether the pins show,
 * and how Kommentieren announces itself - and the SDK answers a toggle key
 * pressed inside a frame with `loupe:toggle-mode`, because there the shell owns
 * the mode. Additive: a side on 6 overreads both and keeps its defaults.
 *
 * 8 since 2026-10-21 (D-049, [F-008]): `loupe:show-view` asks the SDK to take
 * the page back to an entry's view - its address, its scroll position, its
 * anchor - and `loupe:view-shown` answers what came of it. Additive, but a side
 * on 7 overreads the question and never answers, and silence is the one answer
 * F-008 forbids; so the shell does not ask an SDK below `SHOW_VIEW_PROTOCOL`
 * and says why instead.
 */
import type { ReviewPrefs } from "../shared/review-prefs";
import type { AppContext, ClientInfo } from "../shared/app-context";
import type { FrozenResource } from "../shared/still-resources";

export const LOUPE_PROTOCOL = 8;

/** The first protocol whose SDK answers `loupe:show-view` (D-049). */
export const SHOW_VIEW_PROTOCOL = 8;

/**
 * The WebSocket close code Loupe ends a socket with when its own process stops
 * - `1012 Service Restart` in the IANA registry, which is exactly what it is.
 * The message before it is what a side reads; the code is what a proxy log or
 * the browser's network panel shows.
 */
export const RESTART_CLOSE = 1012;

export type NoteKind = "defect" | "change" | "question" | "praise";

/**
 * What the SDK knows about an entry it draws a pin for.
 *
 * `title` is what the pin shows on hover - an entry has no title field, so it
 * is the id and the first line of the text (trial 2026-09-10). `own` decides
 * what a click opens: the editor for the viewer's own entry, a reading card
 * for anybody else's.
 */
export interface PinInfo {
  id: string;
  label: string;
  anchor: Anchor;
  title?: string;
  body?: string;
  kind?: NoteKind;
  own?: boolean;
}

/** How long an entry's title may get before it is cut. A pin label, not a paragraph. */
const TITLE_MAX = 60;

/**
 * The title of an entry: its first line, cut at a word where one is near.
 *
 * Here and not on either side, because both sides draw it - the shell for a
 * framed application, the SDK for one in its own window - and two copies of
 * "the first line, shortened" drift apart by the first edit.
 */
export function entryTitle(body: string): string {
  const first = (body.split(/\r?\n/).find((line) => line.trim().length > 0) ?? "").trim();
  if (first.length <= TITLE_MAX) return first;
  const cut = first.slice(0, TITLE_MAX);
  const space = cut.lastIndexOf(" ");
  return `${(space > TITLE_MAX * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * Where a pin's centre goes: on the spot the reviewer clicked ([F-001] V0.4,
 * AC-091) - the top-right corner for an anchor without one - and never so close
 * to the document's edge that part of the pin hangs outside it.
 *
 * What hangs outside a document widens it. A pin centred on the edge of
 * something as wide as the page - a page note sits on the body's corner - put
 * half of itself past the right edge: a horizontal scrollbar, then a vertical
 * one for the height the first took (reported 2026-09-15 on LP-016, a page
 * note). Kept inside, a pin moves by at most half its own size and still sits
 * on its element.
 *
 * Here and not on either side for the reason `entryTitle` is: the SDK draws
 * pins on the live page and the shell draws one into the rebuilt still.
 */
export function pinCentre(
  rect: Anchor["rect"],
  spot: Anchor["spot"],
  pin: { w: number; h: number },
  page: { w: number; h: number },
): { x: number; y: number } {
  const x = rect.x + (spot ? rect.w * spot.fx : rect.w);
  const y = rect.y + (spot ? rect.h * spot.fy : 0);
  // A page narrower than a pin has no inside to keep it in; leave it where it points.
  const inside = (value: number, half: number, extent: number) =>
    extent >= 2 * half ? Math.min(Math.max(value, half), extent - half) : value;
  return { x: inside(x, pin.w / 2, page.w), y: inside(y, pin.h / 2, page.h) };
}

/**
 * How the two halves reach each other. Q10 (2026-08-21) added the second one.
 *
 * - `frame`  - the app is inside Loupe's shell; messages go by postMessage.
 * - `socket` - the app is a top-level window opened by the dashboard; messages
 *   go through the server over a WebSocket.
 *
 * The second exists because a cross-site iframe drops the application's own
 * `SameSite=Lax` session cookie and a top-level window keeps it - measured,
 * not assumed. Everything above the transport is identical, which is the
 * point: the capture core does not know which one it is on.
 */
export type Transport = "frame" | "socket";

/** Where a note was written. Resolvable again after the markup changed. */
export interface Anchor {
  /** The most stable handle we found, in the order we prefer them. */
  strategy: "testid" | "id" | "selector";
  /** data-testid value, when the element carries one. */
  testid: string | null;
  /** CSS path, always filled, used when the strategy above misses. */
  selector: string;
  /** Tag name, cheap sanity check on re-resolution. */
  tag: string;
  /** First 120 characters of the element's own text, for a fuzzy match. */
  text: string;
  /** Document-space box at capture time - where the pin is drawn. */
  rect: { x: number; y: number; w: number; h: number };
  /**
   * Where inside the element the reviewer clicked, as a FRACTION of its box
   * ([F-001] V0.4, AC-091). Held proportionally rather than in pixels so the
   * pin returns to the same spot when the element reflows at another window
   * size - a pixel offset would walk away from what it points at.
   *
   * `null` for anchors captured before V0.4; those fall back to the element's
   * top-right corner, which is where every pin used to sit.
   */
  spot: { fx: number; fy: number } | null;
}

/** Where the reviewer was when they wrote the note. */
export interface ViewState {
  href: string;
  route: string;
  title: string;
  viewport: { w: number; h: number; dpr: number; orientation: "landscape" | "portrait" };
  /**
   * Where the page was scrolled to (protocol 3). The viewer reopens a still
   * at this position, so it shows what the reviewer was looking at rather than
   * the top of the document. Absent on views captured before 2026-09-10.
   */
  scroll?: { x: number; y: number };
  /** Whatever the reviewed app told us about its build, or null. */
  build: string | null;
  /**
   * What the application says about its logged-in account, read when the view
   * is taken (F-013, D-054). Absent when it says nothing - never an empty role.
   */
  app?: AppContext;
  /** The browser, its system and language, as the SDK reads them. */
  client?: ClientInfo;
  capturedAt: string;
}

export type Mode = "browse" | "annotate";

/** Shell -> SDK. */
export type ShellMessage =
  | { type: "loupe:hello"; protocol: number }
  | { type: "loupe:set-mode"; mode: Mode }
  | { type: "loupe:start-record" }
  | { type: "loupe:stop-record" }
  | { type: "loupe:set-pins"; pins: PinInfo[] }
  | { type: "loupe:focus-pin"; id: string }
  | { type: "loupe:cancel-pending" }
  /** Protocol 7: the reviewer's keys and marks (shared/review-prefs.ts). */
  | { type: "loupe:set-prefs"; prefs: ReviewPrefs }
  /**
   * Protocol 8 (D-049, [F-008]): take the page to an entry's view and answer
   * with `loupe:view-shown`, echoing `request`. `navigate: false` says the
   * address was loaded once already for this request - answer where the page
   * is instead of loading it again, so an application that redirects cannot
   * send the two halves round in a circle.
   */
  | {
      type: "loupe:show-view";
      request: string;
      href: string;
      scroll: { x: number; y: number } | null;
      anchor: Anchor;
      navigate: boolean;
    };

/**
 * What the SDK made of `loupe:show-view` (protocol 8):
 *
 * - `shown`          on the view, scrolled, the anchor found the way it was stored, and marked
 * - `shown-weak`     the same, but found only by a weaker route - perhaps not the same element
 * - `anchor-hidden`  on the view; the element is there and renders no box (a closed tab, a folded section)
 * - `anchor-missing` on the view; the element is not on the page
 * - `navigating`     the address differed and a new document is loading it - its SDK announces
 *                    itself, and the shell asks again with `navigate: false`
 * - `other-route`    asked not to navigate, and the page is on another address - the
 *                    application went elsewhere (a record that is gone, a login)
 * - `other-origin`   the entry was written on another origin, which this page cannot load into itself
 */
export type ShowViewResult =
  | "shown"
  | "shown-weak"
  | "anchor-hidden"
  | "anchor-missing"
  | "navigating"
  | "other-route"
  | "other-origin";

/**
 * The socket envelope. The hub tags every message with who sent it and which
 * room it belongs to - a connection's, or a project overview's - so an agent
 * can reconnect into a review that is already running.
 */
export interface Envelope {
  room: string;
  from: "agent" | "dashboard" | "server";
  payload: SdkMessage | ShellMessage | ServerMessage;
}

/** Hub -> everyone in the room. Not produced by either half directly. */
export type ServerMessage =
  | { type: "loupe:agent-presence"; connected: boolean; at: string }
  | { type: "loupe:note-created"; note: unknown }
  /** An entry changed - its author edited it, or it was assigned (protocol 3). */
  | { type: "loupe:note-updated"; note: unknown }
  /**
   * Loupe's own process is stopping (protocol 5, D-024) - the container is
   * being swapped or restarted. The socket closes right after. Whoever reads
   * it reconnects as before, but can now say that the silence is a restart and
   * not a lost connection ([F-014], [NF-012]).
   */
  | { type: "loupe:server-restart"; at: string };

/** SDK -> shell. */
export type SdkMessage =
  /** `reply` (protocol 4): this ready answers a hello, and must not be greeted back (D-017). */
  | { type: "loupe:ready"; protocol: number; view: ViewState; reply?: boolean }
  | { type: "loupe:navigated"; view: ViewState }
  /** `snapshot` is F-009's still, serialized at the click. Null when the
   *  document resisted serialization - the note is saved without one. */
  | { type: "loupe:anchor"; anchor: Anchor; view: ViewState; snapshot?: unknown }
  /*
   * D-033: the still's images, fonts and stylesheets, fetched after the
   * capture and sent when they are in - the editor does not wait for them.
   * `capturedAt` is the anchor's `view.capturedAt`, so a late answer to an
   * earlier click is recognised and dropped. A shell that does not know it
   * ignores it, and the still keeps its URLs.
   */
  | { type: "loupe:still-resources"; capturedAt: string; resources: FrozenResource[] }
  | { type: "loupe:record-state"; recording: boolean; events: number }
  | {
      type: "loupe:recording";
      meta: {
        startedAt: string;
        stoppedAt: string;
        viewport: ViewState["viewport"];
        route: string;
        build: string | null;
        app?: AppContext;
        client?: ClientInfo;
      };
      events: unknown[];
    }
  | { type: "loupe:pin-clicked"; id: string }
  /** Protocol 7: the toggle key was pressed in the application, inside a frame - the shell switches. */
  | { type: "loupe:toggle-mode" }
  | { type: "loupe:anchor-status"; stale: string[] }
  /** Protocol 8: the answer to `loupe:show-view` - and where the page is, and its build, when it answered. */
  | { type: "loupe:view-shown"; request: string; result: ShowViewResult; route: string; build: string | null }
  /**
   * Socket transport only: the reviewer wrote the note in the application's
   * own window, so the note arrives finished rather than as a bare anchor.
   */
  | {
      type: "loupe:note";
      body: string;
      kind: NoteKind;
      anchor: Anchor;
      view: ViewState;
      recordingId: string | null;
    };
